import { computed, onBeforeUnmount, onMounted, ref, watch, type WritableComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDialogListener } from '@/composables/useEventBasedDialog';
import useNotify from '@/composables/useNotify';

/**
 * Value for the "Other..." radio option, that comes from the free-text field. A Symbol rather than
 * the "Other..." label so it cannot collide with `reasonOptions` that come from the caller.
 */
export const OTHER_REASON = Symbol('other-reason');

export type SelectedReason = string | typeof OTHER_REASON | null;

/**
 * State for the task cancellation drawer: the reason the driver picks (or types), and the
 * confirmation handshake with the caller-supplied `onConfirm`.
 */
export function useCancelTaskDrawer() {
  const { t } = useI18n();
  const { notify } = useNotify();

  const {
    isDialogOpen,
    dialogProps,
    closeDialog,
    resetDialogProps,
    subscribeToDialogEvent,
    unsubscribeFromDialogEvent
  } = useDialogListener('open-cancel-task-drawer');

  const selectedReason = ref<SelectedReason>(null);
  const customReason = ref('');
  const isLoading = ref(false);

  const isDrawerOpen: WritableComputedRef<boolean> = computed({
    get: () => isDialogOpen.value,
    set: (nextIsDrawerOpen: boolean) => {
      if (!nextIsDrawerOpen) {
        closeDialog();
      }
    }
  });

  const subtitle = computed(() => dialogProps.value?.subtitle ?? '');
  const reasonOptions = computed(() => dialogProps.value?.reasonOptions ?? []);
  const isOtherReason = computed(() => selectedReason.value === OTHER_REASON);

  const canConfirm = computed(() => {
    if (!selectedReason.value) {
      return false;
    }

    if (isOtherReason.value) {
      return customReason.value.trim().length > 0;
    }

    return true;
  });

  function getReasonText(): string {
    if (isOtherReason.value) {
      return customReason.value.trim();
    }

    return typeof selectedReason.value === 'string' ? selectedReason.value : '';
  }

  function resetForm(): void {
    selectedReason.value = null;
    customReason.value = '';
  }

  function handleAfterLeave(): void {
    resetForm();
    resetDialogProps();
  }

  async function handleConfirm(): Promise<void> {
    const onConfirm = dialogProps.value?.onConfirm;

    if (isLoading.value || !canConfirm.value || !onConfirm) {
      return;
    }

    isLoading.value = true;

    try {
      await onConfirm(getReasonText());
      closeDialog();
    } catch (error) {
      console.error('[useCancelTaskDrawer] Failed to cancel task:', error);
      notify(t('yard.drawers.cancelTaskDrawer.messages.failedToCancelTask'), 'error');
    } finally {
      isLoading.value = false;
    }
  }

  watch(isDialogOpen, isOpen => {
    if (isOpen) resetForm();
  });

  onMounted(() => {
    subscribeToDialogEvent();
  });

  onBeforeUnmount(() => {
    unsubscribeFromDialogEvent();
  });

  return {
    isDrawerOpen,
    subtitle,
    reasonOptions,
    selectedReason,
    customReason,
    isLoading,
    isOtherReason,
    canConfirm,

    closeDialog,
    handleAfterLeave,
    handleConfirm
  };
}
